import type {
  AdminTranslationUsageSummary,
  AdminUserUsageSummary,
  TranslationProvider,
  TranslationProviderUsage,
  TranslationUsagePeriod
} from '@sky-lingo-v2/shared';
import { readAllAdminRows } from './readAdminRows';

export type UsagePeriodKey = 'today' | 'threeDays' | 'month';
export const usagePeriods: Array<{ key: UsagePeriodKey; label: string }> = [
  { key: 'today', label: '今日' },
  { key: 'threeDays', label: '近三日' },
  { key: 'month', label: '本月' }
];

export function selectedUsagePeriod(summary: AdminTranslationUsageSummary | null, key: UsagePeriodKey): TranslationUsagePeriod | null {
  return summary?.periods?.[key] ?? null;
}

export function usageNumber(value: number | null | undefined, missing = '未记录'): string {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? new Intl.NumberFormat('zh-CN').format(value) : missing;
}

export function usageTime(value: string | null | undefined): string {
  if (!value) return '未上报';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '未上报';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).format(date);
}

export function supplierMeasurement(row: TranslationProviderUsage | null, provider: TranslationProvider) {
  const token = provider === 'hunyuan' || provider === 'qwen' || provider === 'index35b' || provider === 'index2b';
  if (!row) return { token, label: '供应商用量', value: '待加载', note: '分期统计尚未加载。', input: '未记录', output: '未记录', incomplete: false };
  if (provider === 'bing') return { token, label: '供应商计费指标', value: '未提供',
    note: 'Bing legacy 不返回供应商 token 或计费字符；仅记录成功调用、原文字符与应用扣额，不推算供应商账单。',
    input: '未提供', output: '未提供', incomplete: false };
  if (row.requestCount === 0) return { token, label: token ? '供应商 token' : '供应商字符', value: '无已记录成功调用', note: '仅表示当前采集范围内没有成功记录。', input: '未记录', output: '未记录', incomplete: false };
  if (token) {
    const recorded = row.tokenRecordedRequests ?? 0;
    const missing = row.tokenMissingRequests ?? row.requestCount;
    const known = [
      { value: row.inputTokens, label: '输入' }, { value: row.outputTokens, label: '输出' },
      { value: row.totalTokens, label: '合计' }
    ].filter(item => typeof item.value === 'number' && Number.isFinite(item.value) && item.value >= 0).map(item => `${item.label} ${usageNumber(item.value)} token`);
    const coverage = recorded > 0 ? `完整指标 ${usageNumber(recorded)} 笔 · 已记录合计 ${usageNumber(row.totalTokens)} token`
      : known.length > 0 ? `已记录${known.join('、')}；其余 token 指标未记录` : 'token 未记录';
    return {
      token, label: '供应商 token', value: usageNumber(row.totalTokens),
      input: usageNumber(row.inputTokens), output: usageNumber(row.outputTokens),
      note: `${coverage}${missing > 0 ? ` · 缺 ${usageNumber(missing)} 笔完整指标，显示值非完整用量` : ''}`,
      incomplete: missing > 0
    };
  }
  const exact = row.billingMeasurement === 'provider';
  return {
    token, label: exact ? '供应商返回计费字符' : '原文计费字符估算',
    value: usageNumber(row.billedCharacters ?? row.inputCharacters), input: '', output: '',
    note: exact ? '按供应商返回值记录，尚未同步账单。'
      : row.billingMeasurement === 'mixed' ? `含 ${usageNumber(row.billingMissingRequests)} 笔原文估算记录，以供应商账单为准。`
      : provider === 'google' ? '按原文 Unicode 码点计算，尚未同步账单。' : '按原文码点估算，以供应商账单为准。',
    incomplete: row.billingMeasurement === 'mixed'
  };
}

export function supplierFailureTokens(row: TranslationProviderUsage | null, provider: TranslationProvider) {
  if (!row || row.failureCount <= 0 || !['hunyuan', 'qwen', 'index35b', 'index2b'].includes(provider)) return null;
  return {
    input: usageNumber(row.failureInputTokens, '未提供'),
    output: usageNumber(row.failureOutputTokens, '未提供'),
    total: usageNumber(row.failureTotalTokens, '未提供'),
    recorded: usageNumber(row.failureTokenRecordedRequests ?? 0),
    missing: usageNumber(row.failureTokenMissingRequests ?? row.failureCount),
  };
}

export function safeUsageMessage(value: string | null | undefined): string {
  return String(value || '').replace(
    /((?:api[_-]?key|access[_-]?key(?:[_-]?(?:id|secret))?|secret[_-]?(?:id|key)|token|authorization)\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^\s,;]+)/gi,
    '$1[已隐藏]'
  ).replace(/Bearer\s+[^\s,;]+/gi, 'Bearer [已隐藏]');
}

export function providerUsageStatus(row: TranslationProviderUsage | null) {
  if (!row) return { label: '未采集', className: 'pending', message: '等待用量与接口状态记录。', code: '' };
  if (row.channel?.paused) return {
    label: row.channel.permanent ? '已暂停 · 需处理' : '已暂停', className: 'warn',
    message: pausedChannelExplanation(row.channel.code, row.channel.category, row.channel.retryAfterMs),
    code: safeUsageMessage(row.channel.code)
  };
  const rawMessage = row.statusMessage || row.lastFailureMessage;
  const diagnostic = /^[a-z][a-z0-9_]+$/.test(rawMessage || '') && rawMessage.includes('_');
  const knownMessage = rawMessage === 'provider_check_ok'
    ? row.statusSource === 'health_check' ? '最近一次接口检测成功。' : '最近一次供应商调用成功。'
    : rawMessage?.startsWith('provider_') ? pausedChannelExplanation(rawMessage, '', row.channel?.retryAfterMs || 0)
    : rawMessage === 'translation_request_invalid' ? '翻译请求参数不正确，请检查原文及目标语言。'
    : rawMessage === 'character_quota_not_enough' ? '应用字符额度不足，请检查用户套餐与额度。'
    : rawMessage === 'application_usage_charge_failed' ? '供应商调用成功，但应用扣额失败，请检查用户额度与账本。'
    : '';
  return {
    label: row.ok === true ? '正常' : row.ok === false ? '异常' : '未采集',
    className: row.ok === true ? 'ok' : row.ok === false ? 'warn' : 'pending',
    message: knownMessage || (diagnostic ? '接口状态需要检查，请到「翻译服务」查看配置与检测结果。' : safeUsageMessage(rawMessage)) || '尚无接口状态记录。',
    code: diagnostic && rawMessage !== 'provider_check_ok' ? safeUsageMessage(rawMessage) : ''
  };
}

function pausedChannelExplanation(code: string, category: string, retryAfterMs: number): string {
  const cooldown = retryAfterMs > 0 ? `请等待 ${Math.ceil(retryAfterMs / 1000)} 秒冷却后再试。` : '请等待冷却结束后再试。';
  if (code === 'provider_access_restricted') return '公共翻译通道暂时拒绝访问，请切换其他模型；需管理员到「翻译服务」手动检测恢复。';
  if (category === 'quota_exhausted' || code === 'provider_quota_exhausted') return '供应商资源包或额度已耗尽，请续费后到「翻译服务」手动检测恢复。';
  if (category === 'authentication' || code === 'provider_authentication_failed') return '供应商认证失效，请到「翻译服务」检查密钥与配置，确认后手动检测。';
  if (category === 'configuration' || code === 'provider_configuration_invalid') return '供应商配置不可用，请到「翻译服务」检查接口地址、模型与密钥，确认后手动检测。';
  if (category === 'rate_limited' || /rate_limited/.test(code)) return `供应商暂时限流。${cooldown}`;
  if (category === 'timeout' || /timeout|timed_out/.test(code)) return `供应商响应超时。${cooldown}`;
  if (category === 'provider_unavailable' || code === 'provider_temporarily_unavailable') return `供应商暂时不可用。${cooldown}`;
  return '供应商通道已暂停，请到「翻译服务」查看配置与接口状态后处理。';
}

type UsageReader = <T>(path: string, options?: RequestInit) => Promise<T>;

// Keep the cumulative user ledger available when the new summary route is absent
// during deployment. Both requests are reads; refreshing never checks a provider.
export async function readUsageView(read: UsageReader, canRead: boolean) {
  if (!canRead) throw new Error('usage_permission_required');
  const [users, summary] = await Promise.allSettled([
    readAllAdminRows(async (page, pageSize) => (await read<{ usage: AdminUserUsageSummary[] }>(
      `/api/admin/usage?page=${page}&pageSize=${pageSize}`, { method: 'GET' }
    )).usage, row => row.userId),
    read<AdminTranslationUsageSummary>('/api/admin/translation-usage', { method: 'GET' })
  ]);
  if (users.status === 'rejected') throw users.reason;
  if (summary.status === 'rejected' && [401, 403].includes(Number(summary.reason?.status))) throw summary.reason;
  // Page on stable user IDs at the API, then retain the cumulative ledger's
  // original ranking only after the complete list has been read.
  const lastUsed = (row: AdminUserUsageSummary) => {
    const value = row.lastUsedAt ? Date.parse(row.lastUsedAt) : NaN;
    return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
  };
  return {
    usage: users.value.sort((a, b) => b.totalCharacters - a.totalCharacters || lastUsed(b) - lastUsed(a) || b.userId - a.userId),
    summary: summary.status === 'fulfilled' ? summary.value : null,
    summaryError: summary.status === 'fulfilled' ? '' : Number(summary.reason?.status) === 404
      ? '分期用量接口尚未就绪，用户累计字符仍可查看。'
      : '暂时无法更新分期用量，用户累计字符仍可查看。'
  };
}
